import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "@/lib/supabase";
import { useSiteSettings } from "@/hooks/useSiteSettings";

type Mode = "signin" | "forgot" | "reset";

export default function AuthForm({ initialMode = "signin" }: { initialMode?: Mode }) {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const { site } = useSiteSettings();

  // Detect the "password recovery" event: after an admin clicks the reset link
  // in their email, Supabase redirects them back here and fires this event so
  // we can show the "choose a new password" form.
  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        setMode("reset");
        setPassword("");
        setError("");
        setNotice("Almost there — choose a new password for your account.");
      } else if (event === "SIGNED_IN" && session) {
        setMode("signin");
      }
    });
    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  const redirectUrl = () => {
    const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
    const pathPrefix = basePath ? `/${basePath}` : "";
    return `${window.location.origin}${pathPrefix}/admin`;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setNotice("");

    if (mode === "forgot") {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: redirectUrl(),
      });
      setLoading(false);
      if (resetError) {
        setError(resetError.message);
      } else {
        setNotice(
          "If an account exists for that email, a password reset link has been sent. Check your inbox (and spam folder) and follow the link."
        );
      }
      return;
    }

    if (mode === "reset") {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      setLoading(false);
      if (updateError) {
        setError(updateError.message);
      } else {
        setNotice("Password updated. You can now sign in below.");
        setMode("signin");
        setPassword("");
      }
      return;
    }

    // signin
    const { error: signinError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    setLoading(false);
    if (signinError) setError(signinError.message);
  };

  const submitLabel =
    mode === "signin" ? "Sign In" : mode === "forgot" ? "Send Reset Link" : "Update Password";

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-dark px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-lg bg-brand-gold text-brand-dark">
            <i className="ri-car-line text-3xl"></i>
          </span>
          <h1 className="mt-4 font-heading text-2xl font-semibold uppercase tracking-wide text-white">
            {site.site_name}
          </h1>
          <p className="mt-1 text-sm text-white/50">Admin Control Panel</p>
        </div>

        <div className="mt-8 rounded-lg bg-white p-6 md:p-8">
          <h2 className="font-heading text-lg font-semibold uppercase text-brand-dark">
            {mode === "signin" ? "Sign In" : mode === "forgot" ? "Reset Password" : "New Password"}
          </h2>
          <p className="mt-1 text-sm text-neutral-500">
            {mode === "signin"
              ? "Enter your admin credentials to continue."
              : mode === "forgot"
                ? "Enter your registered email and we'll send you a reset link."
                : "Set a new password for your account."}
          </p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            {mode !== "reset" && (
              <div>
                <label className="block text-sm font-medium text-brand-dark">Email</label>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                  placeholder="you@topgearautospares.com"
                />
              </div>
            )}

            {(mode === "signin" || mode === "reset") && (
              <div>
                <label className="block text-sm font-medium text-brand-dark">
                  {mode === "reset" ? "New Password" : "Password"}
                </label>
                <input
                  type="password"
                  required
                  minLength={8}
                  autoComplete={mode === "reset" ? "new-password" : "current-password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
                  placeholder="••••••••"
                />
                {mode === "reset" && (
                  <p className="mt-1 text-xs text-neutral-400">Use at least 8 characters.</p>
                )}
              </div>
            )}

            {error && (
              <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}
            {notice && (
              <div className="rounded-md bg-accent-100 border border-accent-300 px-4 py-3 text-sm text-accent-900">
                {notice}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-brand-gold px-6 py-3 text-sm font-semibold uppercase tracking-wide text-brand-dark transition hover:bg-brand-dark hover:text-white disabled:opacity-60 whitespace-nowrap"
            >
              {loading && <i className="ri-loader-4-line animate-spin"></i>}
              {loading ? "Please wait…" : submitLabel}
            </button>
          </form>

          <div className="mt-5 border-t border-neutral-100 pt-4 text-center">
            {mode === "signin" ? (
              <button
                type="button"
                onClick={() => {
                  setMode("forgot");
                  setError("");
                  setNotice("");
                }}
                className="text-sm font-medium text-brand-gold hover:text-brand-dark transition-colors"
              >
                Forgot your password?
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setMode("signin");
                  setError("");
                  setNotice("");
                }}
                className="text-sm font-medium text-brand-gold hover:text-brand-dark transition-colors"
              >
                Back to sign in
              </button>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-white/40">
          This area is restricted to authorised {site.site_name} staff only.
        </p>
      </div>
    </div>
  );
}